import { Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import { ImageDetailsService } from '../../../../services/images-details.service';
import ImageDetailsModel from '../../../../services/images-details.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
declare var $: any;

/**
 * @component ImagesDetailsComponent
 * @selector app-images-details
 * @implements {OnInit}
 *
 * @description
 * The `ImagesDetailsComponent` serves as a dashboard for viewing and managing images related to farm activities.
 * It aggregates images from various sources such as sampling, check trays, and observations, providing a centralized
 * gallery view.
 *
 * ## Key Responsibilities:
 * 1. **Image Aggregation**: Fetches image metadata and counts from multiple farm sites associated with the user's business.
 * 2. **Categorization**: Groups images into categories: Sampling, Check Tray (CTI), and Observation.
 * 3. **Gallery View**: Displays images in a carousel or grid format, allowing users to browse through recent uploads.
 * 4. **Filtering & Sorting**: Provides sorting capabilities to organize images by Farm Name, Section Name, Pond Name, or Location.
 * 5. **Detail View**: Allows users to view full-size images and associated metadata in a modal.
 * 6. **Download**: Supports downloading images from the server (S3 bucket).
 * 7. **Delete**: Allows authorized users to delete images.
 * 8. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to images from all farm sites in their business.
 *   - **Farm Manager**: Restricted access to images from farm sites assigned to them.
 * - **Provider Category Users**:
 *   - **Business Admin**: Access to images from farm sites associated with the provider business.
 * - **Other Categories**:
 *   - **Employee**: Access to all farm sites (typically for internal admin/support).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and fetches relevant farm sites.
 * 2. **Data Fetching**: `getAlltodayImagesCount` is called with the list of farm site IDs to get image metadata.
 * 3. **Display**: Data is processed into `DBImagesDisplay` and category-specific arrays (`checktryImageRec`, `sampleTypeImagesRec`, `observationImageRec`).
 * 4. **Interaction**: Users can click on image groups to view details (`displayImageTypeTable`) or individual images (`getAllImageDownload`).
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad` manages loading indicators.
 * - **Sorting**: Flags like `imagepondname`, `imageFarmname` manage sort direction.
 * - **Modals**: Manages modals for image viewing and sub-tables.
 */
@Component({
  selector: 'app-images-details',
  templateUrl: './images-details.component.html',
  styleUrls: ['./images-details.component.scss']
})
export class ImagesDetailsComponent implements OnInit {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<ImageDetailsModel>} List of image details for display or processing. */
  public imageDetails: Array<ImageDetailsModel>;

  /** @type {ImageDetailsModel} Single image detail model (unused in current logic). */
  public imageDetail: ImageDetailsModel;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad = false;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {number} Count of sampling images. */
  public samplingImageCount: number;

  /** @type {number} Count of check tray images. */
  public ctiImageCount: number;

  /** @type {number} Count of observation images. */
  public observationImageCount: number;

  /** @type {any[]} Main array for displaying aggregated image groups. */
  public DBImagesDisplay: any[];

  /** @type {number} Current index for image carousel. */
  public carouselindex: number;

  /** @type {Array<any>} Array of check tray image records. */
  public checktryImageRec = new Array();

  /** @type {Array<any>} Array of sampling image records. */
  public sampleTypeImagesRec = new Array();

  /** @type {Array<any>} Array of observation image records. */
  public observationImageRec = new Array();

  /** @type {Array<any>} Array for displaying sub-table data (specific category). */
  public subDBImagesDisplay = new Array();

  /** @type {string} Title for the sub-table display. */
  public subDBImagesTitle: string;

  /** @type {boolean} Flag to display the sub-table. */
  public issubDBImagesDisplay: boolean;

  /** @type {Array<any>} Array of farm site IDs for data fetching. */
  public farmSiteIds = new Array();

  /*sort*/
  /** @type {boolean} Flag for sorting by Pond Name (main table). */
  public imagepondname: boolean;

  /** @type {boolean} Flag for sorting by Farm Name (main table). */
  public imageFarmname: boolean;

  /** @type {boolean} Flag for sorting by Location (main table). */
  public imagelocation: boolean;

  /** @type {boolean} Flag for sorting by Section (main table). */
  public imagesection: boolean;

  /*sort subDBImages*/
  /** @type {boolean} Flag for sorting by Pond Name (sub table). */
  public subDBImagesimagepondname: boolean;

  /** @type {boolean} Flag for sorting by Farm Name (sub table). */
  public subDBImagesimageFarmname: boolean;

  /** @type {boolean} Flag for sorting by Location (sub table). */
  public subDBImagesimagelocation: boolean;

  /** @type {boolean} Flag for sorting by Section (sub table). */
  public subDBImagesimagesection: boolean;

  // pagination sorting
  /** @type {number} Current page number for main table. Default 1. */
  public currentPageNumber: number = 1;

  /** @type {number} Items per page for main table. Default 20. */
  public noOfItemsPerPage: number = 20;

  // smapling pagination
  /** @type {number} Current page number for sampling/sub table. Default 1. */
  public currentSampPageNumber: number = 1;

  /** @type {number} Items per page for sampling/sub table. Default 20. */
  public noOfSampItemsPerPage: number = 20;

  /* End pagination, sorting, filter*/

  /**
   * Constructor for ImagesDetailsComponent.
   * Initializes services.
   *
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {ImageDetailsService} imageDetailService - Service for image details operations.
   * @param {DomSanitizer} sanitizer - Service for sanitizing URLs.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   *
   * @service FarmSiteService
   * @service ImageDetailsService
   * @service DomSanitizer
   * @service Sidenavaction
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   * @service BusinessEntityService
   */
  constructor(public farmsiteEntityService: FarmSiteService, public imageDetailService: ImageDetailsService, public sanitizer: DomSanitizer, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, private businessEntityService: BusinessEntityService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory` and `loginPersonType`.
   * - Fetches farm sites and then triggers image count fetching.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.carouselindex = 0;
      this.issubDBImagesDisplay = false;
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        this.farmSites = await this.farmsiteEntityService.getFarmeSitesByFarmsId(localStorage.businessID);
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        await this.getAlltodayImagesCount(this.farmSiteIds);
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.farmSites = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(localStorage.businessID);
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        await this.getAlltodayImagesCount(this.farmSiteIds);
      } else {
        this.farmSites = await this.farmsiteEntityService.getAllFarmSites();
        for (let i = 0; i < this.farmSites.length; i++) {
          this.farmSiteIds.push(this.farmSites[i].id);
        }
        await this.getAlltodayImagesCount(this.farmSiteIds);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method getAlltodayImagesCount
   * @async
   * @description Fetches image counts and metadata for the provided farm site IDs.
   * Aggregates data by pond and image type (CTI, Observation, Sampling).
   * Populates `DBImagesDisplay` and category-specific arrays.
   * @param {Array<string>} farmSiteIds - List of farm site IDs to fetch data for.
   * @side-effect Updates `ctiImageCount`, `observationImageCount`, `samplingImageCount`, `DBImagesDisplay`, `checktryImageRec`, `observationImageRec`, `sampleTypeImagesRec`.
   */
  public async getAlltodayImagesCount(farmSiteIds: Array<string>) {
    try {
      let imageDetails = await this.imageDetailService.getImageDetails(farmSiteIds);
      this.ctiImageCount = imageDetails && imageDetails[0] && imageDetails[0].count;
      this.observationImageCount = imageDetails && imageDetails[1] && imageDetails[1].count;
      this.samplingImageCount = imageDetails && imageDetails[2] && imageDetails[2].count;
      this.pgLoad = true;
      this.DBImagesDisplay = [];
      /** code for differnt image type wise data display**/
      this.sampleTypeImagesRec = [];
      this.checktryImageRec = [];
      this.observationImageRec = [];

      if (imageDetails && imageDetails.length > 0) {
        for (let i = 0; i < imageDetails.length; i++) {
          let eachObj = imageDetails[i]; //cti
          let pondidsbygroup = _.groupBy(eachObj.data, 'pond_id');
          let pondidkyes = _.keys(pondidsbygroup);
          for (let pid = 0; pid < pondidkyes.length; pid++) {
            let totalrecordsbyPondid = pondidsbygroup[pondidkyes[pid]];
            if (totalrecordsbyPondid.length > 0) {
              var onepond = pondidsbygroup[pondidkyes[pid]][0]; // first record from group
              onepond['image_type'] = eachObj.image_type;
              onepond['image_count'] = totalrecordsbyPondid.length;
              this.DBImagesDisplay.push(onepond);
              if (eachObj.image_type == 'cti') {
                this.checktryImageRec.push(onepond);
              }
              if (eachObj.image_type == 'obs') {
                this.observationImageRec.push(onepond);
              }
              if (eachObj.image_type == 'sam') {
                this.sampleTypeImagesRec.push(onepond);
              }
            }
          }
        }
        let a = this.DBImagesDisplay;
        this.DBImagesDisplay = _.orderBy(a, ['date_time'], ['desc']);
        let b = this.observationImageRec;
        this.observationImageRec = _.orderBy(b, ['date_time'], ['desc']);
        let c = this.checktryImageRec;
        this.checktryImageRec = _.orderBy(c, ['date_time'], ['desc']);
        let d = this.sampleTypeImagesRec;
        this.sampleTypeImagesRec = _.orderBy(d, ['date_time'], ['desc']);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /*End get today images count*/


  /**
   * @method getAllImageDownload
   * @async
   * @description Fetches full image details and downloads image content (base64) for a specific list item.
   * Used when viewing details of a pond's images.
   * @param {any} list - The list item containing metadata (pond_id, image_type, etc.).
   * @side-effect Updates `imageDetails` with downloaded image data.
   */
  public async getAllImageDownload(list: any) {
    try {
      this.pgLoad = false;
      let inputData = {
          farm_id: list.business_id,
          site_id: list.farm_site_id + '',
          section_id: list.section_id + '',
          pond_id: list.pond_id + '',
          function: list.image_type,
          pond_culture_id: list.pond_culture_id + '',
          updated_on: list.updated_on,
        };
        const controllerName =
          list.image_type === 'sam'
            ? 'samplingimagesdata'
            : list.image_type === 'cti'
            ?  'imagemetadata'
            : 'pondimagesdata';

        this.imageDetails = await this.imageDetailService.downloadImage(inputData, controllerName);
        for (let i = 0; i < this.imageDetails.length; i++) {
          const data = this.imageDetails[i];
          
          const file$ = this.businessEntityService.downloadFileFromS3Bucket(data['filePath']);

          file$.subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              // Add new key "image" with base64 data
              this.imageDetails[i]['image'] = readFile.result as string;
            };
          });
        }
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method transform
   * @description Sanitizes a base64 image string for safe use in the DOM.
   * @param {any} base64Image - The base64 image string.
   * @returns {SafeResourceUrl} The sanitized URL.
   */
  public transform(base64Image: any) {
    return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64, ' + base64Image);
  }

  /** @type {any} Currently selected image object for modal display. */
  selectedImageObj: any;

  /**
   * @method imageHideShow
   * @description Sets the selected image object for display in a modal.
   * @param {any} selectedImageObj - The image object to display.
   */
  public imageHideShow(selectedImageObj) {
    this.selectedImageObj = selectedImageObj;
    // this.imageshow = true;
  }

  /**
   * @method closeModel
   * @description Clears the selected image object, effectively closing the modal.
   */
  public closeModel() {
    this.selectedImageObj = null;
  }

  /**
   * @method carouselClick
   * @description Handles navigation in the image carousel.
   * Updates `carouselindex`.
   * @param {string} type - Navigation direction ('prev' or 'next').
   * @side-effect Updates `carouselindex`.
   */
  public carouselClick(type) {
    if (type == 'prev') {
      if (this.carouselindex == 0) {
        this.carouselindex = this.imageDetails.length - 1;
      } else {
        this.carouselindex = this.carouselindex - 1;
      }
    }
    if (type == 'next') {
      if (this.carouselindex == this.imageDetails.length - 1) {
        this.carouselindex = 0;
      } else {
        this.carouselindex = this.carouselindex + 1;
      }
    }
  }

  /**
   * @method displayImageTypeTable
   * @description Displays a sub-table for a specific image type (Sampling, CTI, Observation).
   * @param {string} type - The image type code ('sam', 'cti', 'obs').
   * @side-effect Updates `issubDBImagesDisplay`, `subDBImagesDisplay`, `subDBImagesTitle`.
   */
  public displayImageTypeTable(type) {
    this.issubDBImagesDisplay = true;
    if (type == 'sam') {
      this.subDBImagesDisplay = this.sampleTypeImagesRec;
      this.subDBImagesTitle = 'Sampling Data';
    }
    if (type == 'cti') {
      this.subDBImagesDisplay = this.checktryImageRec;
      this.subDBImagesTitle = 'Checktray Data';
    }
    if (type == 'obs') {
      this.subDBImagesDisplay = this.observationImageRec;
      this.subDBImagesTitle = 'Observation Data';
    }
  }

  /**
   * @method cancelSubDBImagesDisplay
   * @description Hides the sub-table display.
   * @side-effect Sets `issubDBImagesDisplay` to false.
   */
  public cancelSubDBImagesDisplay() {
    this.issubDBImagesDisplay = false;
  }


  /* End image download */

  /**
   * @method imageDelete
   * @async
   * @description Deletes a specific image record.
   * Prompts for confirmation before deletion.
   * @param {any} list - The image record to delete.
   * @side-effect Calls `imageDetailService.deleteImage`, refreshes image counts.
   */
  public async imageDelete(list: any) {
    try {
      let deleteImage = confirm('Are you sure you want to delete these image?');
      if (deleteImage) {
        let imageDetails = new ImageDetailsModel();
        imageDetails.farm_id = list.farm_id;
        imageDetails.site_id = list.site_id + '';
        imageDetails.section_id = list.section_id + '';
        imageDetails.pond_id = list.pond_id + '';
        imageDetails.pond_culture_id = list.pond_culture_id + '';
        imageDetails.function = list.function + '';
        imageDetails.updated_on = list.Updated_on;
        await this.imageDetailService.deleteImage(imageDetails);
        this.getAlltodayImagesCount(this.farmSiteIds);
        $('#myModal').modal('hide');
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /*image delete end*/

  /**
   * @method sortImage
   * @description Sorts the main image list (`DBImagesDisplay`) based on the specified type.
   * Supports sorting by 'farm', 'section_name', 'pond_name', 'location'.
   * @param {string} type - The field to sort by.
   */
  public sortImage(type) {
    if (type == 'farm') {
      this.imageFarmname = !this.imageFarmname;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.imageFarmname == true) {
        orderbyval = 'asc';
      }
      this.DBImagesDisplay = _.orderBy(this.DBImagesDisplay, ['Business_name'], [orderbyval]);
      this.imagelocation = false;
      this.imagepondname = false;
      this.imagesection = false;
    }
    if (type == 'section_name') {
      this.imagesection = !this.imagesection;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.imagesection == true) {
        orderbyval = 'asc';
      }
      this.DBImagesDisplay = _.orderBy(this.DBImagesDisplay, ['section_name'], [orderbyval]);
      this.imagelocation = false;
      this.imagepondname = false;
      this.imageFarmname = false;
    }
    if (type == 'pond_name') {
      this.imagepondname = !this.imagepondname;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.imagepondname == true) {
        orderbyval = 'asc';
      }
      this.DBImagesDisplay = _.orderBy(this.DBImagesDisplay, ['pond_name'], [orderbyval]);
      this.imagelocation = false;
      this.imagesection = false;
      this.imageFarmname = false;
    }
    if (type == 'location') {
      this.imagelocation = !this.imagelocation;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.imagelocation == true) {
        orderbyval = 'asc';
      }
      this.DBImagesDisplay = _.orderBy(this.DBImagesDisplay, ['location'], [orderbyval]);
      this.imagepondname = false;
      this.imagesection = false;
      this.imageFarmname = false;
    }
  }

  /**
   * @method sortsubDBImages
   * @description Sorts the sub-table image list (`subDBImagesDisplay`) based on the specified type.
   * Supports sorting by 'subDBImagesfarm', 'subDBImagessection_name', 'subDBImagespond_name', 'subDBImageslocation'.
   * @param {string} type - The field to sort by.
   */
  public sortsubDBImages(type) {
    if (type == 'subDBImagesfarm') {
      this.subDBImagesimageFarmname = !this.subDBImagesimageFarmname;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.subDBImagesimageFarmname == true) {
        orderbyval = 'asc';
      }
      this.subDBImagesDisplay = _.orderBy(this.subDBImagesDisplay, ['Business_name'], [orderbyval]);
      this.subDBImagesimagelocation = false;
      this.subDBImagesimagepondname = false;
      this.subDBImagesimagesection = false;
    }
    if (type == 'subDBImagessection_name') {
      this.subDBImagesimagesection = !this.subDBImagesimagesection;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.subDBImagesimagesection == true) {
        orderbyval = 'asc';
      }
      this.subDBImagesDisplay = _.orderBy(this.subDBImagesDisplay, ['section_name'], [orderbyval]);
      this.subDBImagesimagelocation = false;
      this.subDBImagesimagepondname = false;
      this.subDBImagesimageFarmname = false;
    }
    if (type == 'subDBImagespond_name') {
      this.subDBImagesimagepondname = !this.subDBImagesimagepondname;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.subDBImagesimagepondname == true) {
        orderbyval = 'asc';
      }
      this.subDBImagesDisplay = _.orderBy(this.subDBImagesDisplay, ['pond_name'], [orderbyval]);
      this.subDBImagesimagelocation = false;
      this.subDBImagesimagesection = false;
      this.subDBImagesimageFarmname = false;
    }
    if (type == 'subDBImageslocation') {
      this.subDBImagesimagelocation = !this.subDBImagesimagelocation;
      let orderbyval: 'asc' | 'desc' = 'desc';
      if (this.subDBImagesimagelocation == true) {
        orderbyval = 'asc';
      }
      this.subDBImagesDisplay = _.orderBy(this.subDBImagesDisplay, ['location'], [orderbyval]);
      this.subDBImagesimagepondname = false;
      this.subDBImagesimagesection = false;
      this.subDBImagesimageFarmname = false;
    }
  }
}
